import React, {
  type ComponentProps,
  memo,
  useCallback,
  useRef,
  useState,
} from 'react';
import { defineMessages, useIntl } from 'react-intl';
import {
  type NativeScrollEvent,
  type NativeSyntheticEvent,
  type ScrollView,
  StyleSheet,
  View,
} from 'react-native';
import { useSharedValue } from 'react-native-reanimated';
import { useA11ySelector } from '@sg/a11y-helpers';
import { Tabs, theme, useResponsive, useScrollspy } from '@sg/garnish';
import {
  checkIfDressingIngredient,
  CustomizationConfirmExitModal,
  CustomizationIngredientsGrid,
  CustomizationLoadingOverlay,
  CustomizationReview,
  useCustomizationContext,
  useCustomizationHelpers,
  useCustomizationStepperTimeout,
  type useInitCustomizationMachine,
} from '@sg/shared-fe/features/customization';

import {
  CustomizationBottomBar,
  CustomizationContainer,
  CustomizationCTA,
  CustomizationDetailsCompoundContainer,
  CustomizationDetailsContainer,
  CustomizationFloatingBar,
  CustomizationSelectedIngredients,
} from '@order/features/customization';
import { useFeatureFlag } from '@order/LaunchDarkly';
import { type TelemetryEventName, useTelemetry } from '@order/Telemetry';

import {
  NotFoundView,
  useDietaryRestrictions,
} from '../../../../../../components';
import { CUSTOMIZATIONS_TABS } from '../../../../constants';
import { type useCustomizationContent } from '../../../../hooks';
import {
  CustomizationHeader,
  CustomizationModals,
  CustomizationNutritionCalculator,
} from '../../components';
import {
  useConfirmExitCustomization,
  useModificationGroupHelperText,
  useSortedModifications,
} from '../../hooks';
import {
  CustomizationIngredientsGridPanel,
  CustomizationIngredientsReviewPanel,
  CustomizationModificationGroup,
} from './components';
import { useCustomizationTabs } from './hooks';

// ─────────────────────────────────────────────────────────────────────────────

export const CustomizationCustomizeView = memo((props: Props) => {
  const {
    actor,
    addressId,
    shouldDisableControls,
    isOutOfStockModalOpen,
    customizationContent,
    closeOutOfStockModal,
  } = props;

  const navA11ySelector = useA11ySelector('customization.nav');

  // ─── Customization Context ───────────────────────────────────────────

  const {
    addIngredient,
    removeIngredient,
    removeIngredientCompletely,
    endCustomization,
    getFormattedQuantity,
    checkIfCanAddMoreModifications,
    getMaxPortionsForGroup,
  } = useCustomizationHelpers({ actor });

  const {
    product,
    restaurantSlug,
    totalCost,
    calories,
    modifications,
    activeModifications,
    modificationLimits,
    quantity,
    lineItemId,
    isDressingDetailsEnabled,
  } = useCustomizationContext({ actor });

  // ─── Flags ───────────────────────────────────────────────────────────

  const isEditMode = Boolean(lineItemId);

  const isMacronutrientsEnabled = useFeatureFlag(
    'CELS-4323-order-macronutrients-enabled',
  );

  // ─────────────────────────────────────────────────────────────────────

  const { track } = useTelemetry();

  const {
    isConfirmExitModalVisible,
    cancelExit,
    confirmExit,
    onExitModalShow,
    showConfirmExitModal,
    hasActiveModifications,
  } = useConfirmExitCustomization({ actor, restaurantSlug, addressId });

  const { minWidth } = useResponsive();
  const { formatMessage } = useIntl();
  const getSectionHelperText = useModificationGroupHelperText();
  const getRestrictionNotice = useDietaryRestrictions({ referrer: 'Customization' }); // prettier-ignore
  const isModificationGroupHelperTextEnabled = useFeatureFlag('CELS-4762-modification-group-helper-text-enabled'); // prettier-ignore

  const sortedModificationGroups = useSortedModifications({ actor });
  const scrollSpy = useScrollspy({ defaultActiveTargetId: sortedModificationGroups?.[0]?.id }); // prettier-ignore

  const ctaModifyCartVariation: CtaVariation = lineItemId
    ? 'update'
    : 'add-to-bag';
  const ctaVariation: CtaVariation = isDressingDetailsEnabled
    ? 'continue'
    : ctaModifyCartVariation;

  // ─── Refs ────────────────────────────────────────────────────────────

  const detailsContainerRef = useRef<ScrollView>(null);

  // ─── State ───────────────────────────────────────────────────────────

  const [activeIngredientId, setActiveIngredientId] = useState<string>();

  const clearActiveIngredientId = useCallback(() => {
    setActiveIngredientId(undefined);
  }, []);

  // ─── Tabs ────────────────────────────────────────────────────────────

  const { tabs, activeTabItemId, setActiveTabItemId } = useCustomizationTabs(); // prettier-ignore

  const isAddIngredientsActive = activeTabItemId === CUSTOMIZATIONS_TABS.ADD_INGREDIENTS; // prettier-ignore
  const isReviewActive = activeTabItemId === CUSTOMIZATIONS_TABS.REVIEW;

  // ─── Stepper ─────────────────────────────────────────────────────────

  const { activeStepperId, setActiveStepperId, resetStepperTimeout } =
    useCustomizationStepperTimeout();

  // ─── Scrollspy ───────────────────────────────────────────────────────

  const { helpers, activeTargetId, setActiveTargetId } = scrollSpy;

  // ─── Helpers ─────────────────────────────────────────────────────────

  const addIngredientFromReviewTab = useCallback(
    (ingredientId: string) => {
      addIngredient(ingredientId);
      track('customization.review-tab.ingredient-added');
    },
    [addIngredient, track],
  );

  const removeIngredientFromReviewTab = useCallback(
    (ingredientId: string) => {
      removeIngredient(ingredientId);
      track('customization.review-tab.ingredient-removed');
    },
    [removeIngredient, track],
  );

  const removeIngredientCompletelyFromReviewTab = useCallback(
    (ingredientId: string) => {
      removeIngredientCompletely(ingredientId);
      track('customization.review-tab.ingredient-removed');
    },
    [removeIngredientCompletely, track],
  );

  const onAddMoreIngredients = useCallback(
    (groupId: string, groupName: string) => {
      setActiveTargetId(groupId);
      setActiveTabItemId(CUSTOMIZATIONS_TABS.ADD_INGREDIENTS);
      track('customization.review-tab.add-from-section-tapped', { section: groupName }); // prettier-ignore
    },
    [setActiveTabItemId, setActiveTargetId, track],
  );

  const changeTab = useCallback(
    (tab: string) => {
      const isIngredientsTab = tab === CUSTOMIZATIONS_TABS.ADD_INGREDIENTS;
      const eventName: TelemetryEventName = isIngredientsTab
        ? 'customization.ingredients-tab.pressed'
        : 'customization.review-tab.pressed';

      setActiveTabItemId(tab);
      track(eventName);
    },
    [setActiveTabItemId, track],
  );

  // ─── Styles ──────────────────────────────────────────────────────────

  const selectedIngredientsScrollSV = useSharedValue(0);

  const updateIngredientsScrollSV = useCallback(
    (event: NativeSyntheticEvent<NativeScrollEvent>) => {
      selectedIngredientsScrollSV.value = event.nativeEvent.contentOffset.y;
    },
    [selectedIngredientsScrollSV],
  );

  // ─────────────────────────────────────────────────────────────────────

  if (!product || !modifications) {
    return <NotFoundView />;
  }

  // ─── Large Breakpoint ────────────────────────────────────────────────

  if (minWidth.isSM) {
    return (
      <>
        <CustomizationContainer>
          <CustomizationDetailsCompoundContainer.Aside>
            <CustomizationDetailsCompoundContainer.ScrollView
              style={styles.container}
              ref={detailsContainerRef}
              stickyHeaderIndices={[0]}
              onScroll={updateIngredientsScrollSV}
              scrollEventThrottle={16}
              bounces={false}
            >
              <CustomizationHeader
                style={styles.headerSM}
                scrollOffsetSV={selectedIngredientsScrollSV}
                restaurantSlug={restaurantSlug}
                addressId={addressId}
                text={product.name}
                onBackButtonPress={hasActiveModifications ? showConfirmExitModal : undefined} // prettier-ignore
              />

              <CustomizationSelectedIngredients.Container
                wrapperStyle={styles.reviewContainer}
              >
                <CustomizationSelectedIngredients.List>
                  {sortedModificationGroups?.map((group, index) => {
                    const isLastSection =
                      index === product.modifierGroups.length - 1;

                    const ingredientsForThisSection =
                      activeModifications.filter(
                        (modification) =>
                          modification.modificationGroupId === group.id,
                      );

                    const customHelperText = getSectionHelperText({
                      freeAggregateQuantity:
                        modificationLimits[group.name]?.freeAggregateQuantity,
                      ingredientKind: group.modificationIngredientKind,
                    });
                    const helperText = isModificationGroupHelperTextEnabled
                      ? group.helperText
                      : customHelperText;

                    return (
                      <CustomizationSelectedIngredients.IngredientKindSection
                        key={group.id}
                        ingredientKindName={group.displayName}
                        style={isLastSection ? styles.withoutBorder : null}
                        helperText={helperText}
                        formattedQuantity={getFormattedQuantity(group.name)}
                      >
                        {ingredientsForThisSection.map((modification) => {
                          const { ingredient } = modification;

                          if (!ingredient) return null;

                          const isActive = activeIngredientId === ingredient.id;
                          const isDressing = checkIfDressingIngredient(modification); // prettier-ignore
                          const maxPortionsForGroup = getMaxPortionsForGroup(group.name); // prettier-ignore
                          const restrictionNotice = getRestrictionNotice(ingredient.dietaryProperties); // prettier-ignore
                          const shouldDisableStepper = maxPortionsForGroup === 1 || isDressing; // prettier-ignore

                          return (
                            <CustomizationSelectedIngredients.ListItem
                              key={ingredient.id}
                              isActive={isActive}
                              scrollViewRef={
                                detailsContainerRef as React.RefObject<ScrollView>
                              }
                            >
                              <CustomizationSelectedIngredients.IngredientV2
                                ingredientId={ingredient.id}
                                ingredientName={ingredient.name}
                                ingredientAsset={ingredient.asset}
                                quantity={modification.quantity}
                                isActive={isActive}
                                isDisabled={shouldDisableStepper}
                                restrictionNotice={restrictionNotice}
                                setActive={setActiveIngredientId}
                                setInactive={clearActiveIngredientId}
                                onIncreaseQuantity={addIngredient}
                                onDecreaseQuantity={removeIngredient}
                                onRemove={removeIngredientCompletely}
                              />
                            </CustomizationSelectedIngredients.ListItem>
                          );
                        })}

                        <CustomizationReview.AddMoreIngredientsButton
                          modificationGroupId={group.id}
                          modificationGroupName={group.displayName}
                          onPress={onAddMoreIngredients}
                          isDisabled={
                            !checkIfCanAddMoreModifications(group.name)
                          }
                        />
                      </CustomizationSelectedIngredients.IngredientKindSection>
                    );
                  })}
                </CustomizationSelectedIngredients.List>
              </CustomizationSelectedIngredients.Container>
            </CustomizationDetailsCompoundContainer.ScrollView>

            {isMacronutrientsEnabled ? null : (
              <CustomizationFloatingBar>
                <CustomizationCTA
                  variation={ctaVariation}
                  cost={totalCost}
                  quantity={quantity}
                  calories={calories}
                  onPress={endCustomization}
                />
              </CustomizationFloatingBar>
            )}
          </CustomizationDetailsCompoundContainer.Aside>

          {sortedModificationGroups && sortedModificationGroups?.length > 0 ? (
            <View style={styles.ingredientsGridContainer}>
              <CustomizationIngredientsGrid.Nav
                register={helpers.navScrollView.register}
                deregister={helpers.navScrollView.deregister}
                trackScroll={helpers.navScrollView.trackScroll}
                storeSize={helpers.navScrollView.storeSize}
                {...navA11ySelector.props}
              >
                {sortedModificationGroups.map((group) => (
                  <CustomizationIngredientsGrid.NavItem
                    key={group.id}
                    isActive={activeTargetId === group.id}
                    onPress={setActiveTargetId}
                    targetId={group.id}
                    ingredientKindName={group.displayName}
                    register={helpers.navItem.register}
                    deregister={helpers.navItem.deregister}
                  />
                ))}
              </CustomizationIngredientsGrid.Nav>

              <CustomizationIngredientsGrid.Container
                register={helpers.scrollView.register}
                deregister={helpers.scrollView.deregister}
                storeContentSize={helpers.scrollView.storeContentSize}
                storeSize={helpers.scrollView.storeSize}
                trackScroll={helpers.scrollView.trackScroll}
              >
                {sortedModificationGroups?.map((group) => (
                  <CustomizationModificationGroup
                    key={group.id}
                    actor={actor}
                    modificationGroup={group}
                    activeStepperId={activeStepperId}
                    resetStepperTimeout={resetStepperTimeout}
                    setActiveStepperId={setActiveStepperId}
                    scrollSpy={scrollSpy}
                  />
                ))}
              </CustomizationIngredientsGrid.Container>

              {isMacronutrientsEnabled ? (
                <CustomizationBottomBar withGradient>
                  <CustomizationNutritionCalculator.Floating
                    calories={calories}
                    activeModifications={modifications.active}
                    product={product}
                  />

                  <CustomizationCTA
                    variation={ctaVariation}
                    cost={totalCost}
                    quantity={quantity}
                    onPress={endCustomization}
                  />
                </CustomizationBottomBar>
              ) : null}
            </View>
          ) : null}

          <CustomizationLoadingOverlay show={shouldDisableControls} />
        </CustomizationContainer>

        <CustomizationModals
          actor={actor}
          isOutOfStockModalOpen={isOutOfStockModalOpen}
          closeOutOfStockModal={closeOutOfStockModal}
          setScrollTargetId={setActiveTargetId}
        />

        <CustomizationConfirmExitModal
          isVisible={isConfirmExitModalVisible}
          size="md"
          message={customizationContent.fields?.orderCustomizeConfirmExitMessage} // prettier-ignore
          confirmButtonTitle={customizationContent.fields?.orderCustomizeConfirmExitConfirmButtonTitle} // prettier-ignore
          cancelButtonTitle={customizationContent.fields?.orderCustomizeConfirmExitCancelButtonTitle} // prettier-ignore
          target={isEditMode ? formatMessage(messages.bag) : formatMessage(messages.menu)} // prettier-ignore
          onConfirm={confirmExit}
          onCancel={cancelExit}
          onShow={onExitModalShow}
        />
      </>
    );
  }

  // ─── Small Breakpoint ────────────────────────────────────────────────

  return (
    <>
      <CustomizationContainer>
        <View style={styles.headerXS}>
          <CustomizationHeader
            scrollOffsetSV={selectedIngredientsScrollSV}
            restaurantSlug={restaurantSlug}
            addressId={addressId}
            text={product.name}
            spacing="narrow"
            onBackButtonPress={hasActiveModifications ? showConfirmExitModal : undefined} // prettier-ignore
          />

          <Tabs.ButtonBar
            tabs={tabs}
            isFluid={true}
            activeTabId={activeTabItemId}
            onChange={changeTab}
            style={styles.tabButtonsBar}
          />
        </View>

        <CustomizationDetailsContainer
          scrollEventThrottle={16}
          onScroll={updateIngredientsScrollSV}
          bounces={false}
          isFluid={true}
          contentContainerStyle={styles.panelContainer}
        >
          <Tabs.Panel
            isActive={isAddIngredientsActive}
            tabId={CUSTOMIZATIONS_TABS.ADD_INGREDIENTS}
            panelId={CUSTOMIZATIONS_TABS.ADD_INGREDIENTS}
            style={isAddIngredientsActive ? styles.panel : null}
          >
            <CustomizationIngredientsGridPanel
              actor={actor}
              activeTargetId={activeTargetId}
              modificationGroups={sortedModificationGroups}
            />
          </Tabs.Panel>

          <Tabs.Panel
            isActive={isReviewActive}
            tabId={CUSTOMIZATIONS_TABS.REVIEW}
            panelId={CUSTOMIZATIONS_TABS.REVIEW}
            style={isReviewActive ? styles.panel : null}
          >
            <CustomizationIngredientsReviewPanel
              product={product}
              modifications={modifications}
              modifierGroups={sortedModificationGroups}
              modificationLimits={modificationLimits}
              addIngredientModification={addIngredientFromReviewTab}
              removeIngredientModification={removeIngredientFromReviewTab}
              completelyRemoveIngredientModification={removeIngredientCompletelyFromReviewTab} // prettier-ignore
              getFormattedQuantity={getFormattedQuantity}
              onAddMoreIngredients={onAddMoreIngredients}
              checkIfCanAddMore={checkIfCanAddMoreModifications}
            />
          </Tabs.Panel>
        </CustomizationDetailsContainer>

        <CustomizationFloatingBar>
          {isMacronutrientsEnabled ? (
            <CustomizationNutritionCalculator.Floating
              calories={calories}
              activeModifications={modifications.active}
              product={product}
            />
          ) : null}

          <CustomizationCTA
            variation={ctaVariation}
            cost={totalCost}
            quantity={quantity}
            calories={isMacronutrientsEnabled ? undefined : calories}
            onPress={endCustomization}
          />
        </CustomizationFloatingBar>

        <CustomizationLoadingOverlay show={shouldDisableControls} />
      </CustomizationContainer>

      <CustomizationModals
        actor={actor}
        isOutOfStockModalOpen={isOutOfStockModalOpen}
        closeOutOfStockModal={closeOutOfStockModal}
        setScrollTargetId={setActiveTargetId}
      />

      <CustomizationConfirmExitModal
        isVisible={isConfirmExitModalVisible}
        size="md"
        message={customizationContent.fields?.orderCustomizeConfirmExitMessage} // prettier-ignore
        confirmButtonTitle={customizationContent.fields?.orderCustomizeConfirmExitConfirmButtonTitle} // prettier-ignore
        cancelButtonTitle={customizationContent.fields?.orderCustomizeConfirmExitCancelButtonTitle} // prettier-ignore
        target={isEditMode ? formatMessage(messages.bag) : formatMessage(messages.menu)} // prettier-ignore
        onConfirm={confirmExit}
        onCancel={cancelExit}
        onShow={onExitModalShow}
      />
    </>
  );
});

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  menu: {
    defaultMessage: 'menu',
    description: 'Customization | Confirm exit modal | Menu',
  },
  bag: {
    defaultMessage: 'bag',
    description: 'Customization | Confirm exit modal | Bag',
  },
});

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    paddingTop: 0,
  },
  headerXS: {
    backgroundColor: theme.colors.CREAM,
  },
  headerSM: {
    width: '100%',
    flexDirection: 'row',
    paddingVertical: theme.spacing['1'],
    marginBottom: theme.spacing['4'],
  },
  tabButtonsBar: {
    paddingHorizontal: theme.spacing['4'],
  },
  panelContainer: {
    flex: 1,
  },
  panel: {
    flex: 1,
  },
  reviewContainer: {
    marginHorizontal: -theme.spacing['4'],
  },
  ingredientsGridContainer: {
    flex: 1,
  },
  withoutBorder: {
    borderBottomWidth: 0,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = {
  actor: ReturnType<typeof useInitCustomizationMachine>;
  customizationContent: ReturnType<typeof useCustomizationContent>;
  addressId: string | undefined;
  isOutOfStockModalOpen: boolean;
  shouldDisableControls: boolean;
  closeOutOfStockModal: () => void;
};

type CtaVariation = ComponentProps<typeof CustomizationCTA>['variation'];
